class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById("cart-notification");

    if (!this.notification) {
      return;
    }

    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener("keyup", (evt) => {
      if (evt.code === "Escape") this.close();
    });

    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener("click", this.close.bind(this))
    );

    document.addEventListener("cart:updated", (event) => {
      // Don't re-render if this update came from a quantity selector
      if (event.detail && event.detail.source === 'quantity-selector') {
        return;
      }
      this.renderContents(event.detail);
    });

    // Set up event delegation for quantity selectors
    this.setupQuantityHandlers();
  }

  setupQuantityHandlers() {
    // Use event delegation to handle quantity selector changes
    this.addEventListener('change', (event) => {
      if (event.target.classList.contains('quantity-select')) {
        this.updateCartItemQuantity(event.target);
      }
    });
  }

  updateCartItemQuantity(selectElement) {
    const newQuantity = parseInt(selectElement.value);
    const cartItemKey = selectElement.dataset.cartItemKey;
    const variantId = selectElement.dataset.variantId;
    const inventoryQuantity = parseInt(selectElement.dataset.inventoryQuantity);
    const inventoryPolicy = selectElement.dataset.inventoryPolicy;
    const inventoryManagement = selectElement.dataset.inventoryManagement;
    const originalValue = selectElement.dataset.originalValue || selectElement.querySelector('option[selected]')?.value;
    
    // Store current value for potential rollback
    selectElement.dataset.originalValue = originalValue;
    
    // Validate quantity against inventory if managed by Shopify
    if (inventoryManagement === 'shopify' && inventoryPolicy !== 'continue') {
      // Get total available inventory
      const totalInventory = inventoryQuantity;
      
      // Calculate how many of this variant are in cart (excluding this item)
      const allCartItems = Array.from(document.querySelectorAll('.quantity-select'));
      const sameVariantItems = allCartItems.filter(item => 
        item.dataset.variantId === variantId && item !== selectElement
      );
      
      const otherItemsQuantity = sameVariantItems.reduce((sum, item) => {
        return sum + parseInt(item.value || 0);
      }, 0);
      
      // Check if new quantity would exceed available inventory
      const totalRequestedQuantity = newQuantity + otherItemsQuantity;
      
      if (totalRequestedQuantity > totalInventory) {
        const maxAllowed = totalInventory - otherItemsQuantity;
        alert(`Not enough items available. Maximum ${maxAllowed} available for this product.`);
        selectElement.value = originalValue;
        return;
      }
    }
    
    // Disable select to prevent multiple clicks
    selectElement.disabled = true;
    
    // Show loading state on the select
    const originalText = selectElement.options[selectElement.selectedIndex].text;
    selectElement.options[selectElement.selectedIndex].text = `${newQuantity} (Updating...)`;
    
    // Update cart via AJAX
    fetch(window.routes.cart_change_url, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        id: cartItemKey,
        quantity: newQuantity
      })
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.json();
    })
    .then(cartData => {
      // Update the select element's original value
      selectElement.dataset.originalValue = newQuantity.toString();
      
      // If quantity is 0, the item was removed
      if (newQuantity === 0) {
        // Remove the cart item from the notification
        const cartItem = selectElement.closest('.cart-item');
        if (cartItem) {
          cartItem.style.transition = 'opacity 0.3s ease';
          cartItem.style.opacity = '0';
          setTimeout(() => {
            cartItem.remove();
            // Check if cart is now empty
            const remainingItems = this.querySelectorAll('.cart-item');
            if (remainingItems.length === 0) {
              this.close();
            }
          }, 300);
        }
      } else {
        // Update the select option text back to normal
        selectElement.options[selectElement.selectedIndex].text = newQuantity.toString();
      }
      
      // Update cart icon bubble count
      this.updateCartBubble(cartData);
      
      // Trigger cart updated event for other components (but not cart notification render)
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: {
          cart: cartData,
          key: cartItemKey,
          quantity: newQuantity,
          source: 'quantity-selector'
        }
      }));
    })
    .catch(error => {
      console.error('Error updating cart:', error);
      alert('Failed to update quantity. Please try again.');
      // Reset select to previous value
      selectElement.value = originalValue;
      selectElement.options[selectElement.selectedIndex].text = originalText;
    })
    .finally(() => {
      // Re-enable select
      selectElement.disabled = false;
    });
  }

  updateCartBubble(cartData) {
    // Update cart icon bubble in the header
    const cartIconBubble = document.querySelector('#cart-icon-bubble');
    if (cartIconBubble && cartData.item_count !== undefined) {
      // Find or create the cart count bubble
      let cartCountBubble = cartIconBubble.querySelector('.cart-count-bubble');
      
      if (cartData.item_count > 0) {
        if (!cartCountBubble) {
          // Create the bubble if it doesn't exist
          cartCountBubble = document.createElement('div');
          cartCountBubble.className = 'cart-count-bubble';
          cartCountBubble.innerHTML = `
            <span aria-hidden="true">${cartData.item_count}</span>
            <span class="visually-hidden">${cartData.item_count} items in cart</span>
          `;
          cartIconBubble.appendChild(cartCountBubble);
        } else {
          // Update existing bubble
          const countSpan = cartCountBubble.querySelector('span[aria-hidden="true"]');
          const visuallyHiddenSpan = cartCountBubble.querySelector('span.visually-hidden');
          if (countSpan) {
            countSpan.textContent = cartData.item_count;
          }
          if (visuallyHiddenSpan) {
            visuallyHiddenSpan.textContent = `${cartData.item_count} items in cart`;
          }
        }
      } else if (cartCountBubble) {
        // Remove bubble if cart is empty
        cartCountBubble.remove();
      }
    }
    
    // Also update any other cart count displays
    const cartCounts = document.querySelectorAll('[data-cart-count]');
    cartCounts.forEach(element => {
      element.textContent = cartData.item_count;
    });
    
    // Update cart drawer if it exists and is open
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartDrawer.classList.contains('active')) {
      // Trigger cart drawer update
      const cartDrawerItems = cartDrawer.querySelector('cart-drawer-items');
      if (cartDrawerItems && typeof cartDrawerItems.updateQuantity === 'function') {
        cartDrawerItems.updateQuantity();
      }
    }
    
    // Update cart notification button text if it exists
    const cartNotificationButton = document.querySelector('#cart-notification-button');
    if (cartNotificationButton) {
      const buttonText = cartNotificationButton.querySelector('a');
      if (buttonText && cartData.item_count !== undefined) {
        // This should match the translation in cart-notification-button.liquid
        buttonText.textContent = `View cart (${cartData.item_count})`;
      }
    }
  }

  open() {
    if (!this.notification) return;
    this.notification.classList.add("animate", "active");
    document.querySelector("cart-notification").classList.add("active");
    document.body.classList.add("lock");

    this.notification.addEventListener(
      "transitionend",
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener("click", this.onBodyClick);
  }

  close() {
    if (!this.notification) return;
    this.notification.classList.remove("active");
    document.querySelector("cart-notification").classList.remove("active");
    document.body.classList.remove("lock");
    document.body.removeEventListener("click", this.onBodyClick);
    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    if (!parsedState || !this.notification) return;

    // Update cart bubble count even if notification doesn't open
    if (parsedState.cart) {
      this.updateCartBubble(parsedState.cart);
    }

    // Check if we should open the notification based on inventory
    if (!this.shouldOpenNotification(parsedState)) {
      return; // Don't open notification if max inventory is already in cart
    }

    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      const sectionEl = document.getElementById(section.id);
      if (sectionEl && parsedState.sections && parsedState.sections[section.id]) {
        sectionEl.innerHTML = this.getSectionInnerHTML(
          parsedState.sections[section.id],
          section.selector
        );
      }
    });

    // Initialize quantity selectors after content is rendered
    this.initializeQuantitySelectors();

    this.open();
  }



  shouldOpenNotification(parsedState) {
    // If there's no cart data or no items, allow notification
    if (!parsedState.cart || !parsedState.cart.items || parsedState.cart.items.length === 0) {
      return true;
    }

    // Get the variant ID that was just added (from the key)
    const addedItemKey = parsedState.key;
    const addedItem = parsedState.cart.items.find(item => item.key === addedItemKey);
    
    if (!addedItem || !addedItem.variant) {
      return true; // If we can't find the item, allow notification
    }

    const variant = addedItem.variant;
    
    // If inventory is not managed by Shopify or allows continuing when out of stock, allow notification
    if (variant.inventory_management !== 'shopify' || variant.inventory_policy === 'continue') {
      return true;
    }

    // Calculate total quantity of this variant in cart
    const totalVariantInCart = parsedState.cart.items
      .filter(item => item.variant_id === variant.id)
      .reduce((sum, item) => sum + item.quantity, 0);

    // Check if we've reached or exceeded the inventory limit
    const inventoryQuantity = variant.inventory_quantity || 0;
    
    // If total in cart is >= available inventory, don't show notification
    if (totalVariantInCart >= inventoryQuantity) {
      // Show a simple alert instead
      this.showInventoryLimitMessage(addedItem.product_title, inventoryQuantity);
      return false;
    }

    return true; // Allow notification to open
  }

  showInventoryLimitMessage(productTitle, maxQuantity) {
    // Create a simple toast message instead of opening the full notification
    const message = `Maximum quantity (${maxQuantity}) of "${productTitle}" already in cart.`;
    
    // You could replace this with a more sophisticated toast notification
    const toast = document.createElement('div');
    toast.className = 'cart-inventory-toast';
    toast.textContent = message;
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 12px 16px;
      border-radius: 4px;
      z-index: 10000;
      font-size: 14px;
      max-width: 300px;
      opacity: 0;
      transition: opacity 0.3s ease;
    `;
    
    document.body.appendChild(toast);
    
    // Fade in
    setTimeout(() => {
      toast.style.opacity = '1';
    }, 10);
    
    // Fade out and remove
    setTimeout(() => {
      toast.style.opacity = '0';
      setTimeout(() => {
        if (toast.parentNode) {
          toast.parentNode.removeChild(toast);
        }
      }, 300);
    }, 3000);
  }

  initializeQuantitySelectors() {
    const quantitySelects = this.querySelectorAll('.quantity-select');
    quantitySelects.forEach(select => {
      const selectedOption = select.querySelector('option[selected]');
      if (selectedOption) {
        select.dataset.originalValue = selectedOption.value;
      }
    });
  }

  getSectionsToRender() {
    return [
      { 
        id: "cart-notification-all-items"
      },
      { id: "cart-notification-button" },
      { id: "cart-icon-bubble" },
    ];
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");
    return parsedHTML.querySelector(selector)?.innerHTML || "";
  }

  handleBodyClick(evt) {
    if (!this.notification) return;

    const target = evt.target;
    if (target !== this.notification && !target.closest("cart-notification")) {
      const disclosure = target.closest("details-disclosure, header-menu");
      this.activeElement = disclosure
        ? disclosure.querySelector("summary")
        : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-notification", CartNotification);

// Ensure the cart notification updates when a product is added
document.addEventListener("cart:updated", (event) => {
  // Don't re-render if this update came from a quantity selector
  if (event.detail && event.detail.source === 'quantity-selector') {
    return;
  }
  
  // Don't show notification if it should be suppressed due to inventory limits
  if (event.detail && event.detail.suppressNotification) {
    return;
  }
  
  const cartNotification = document.querySelector("cart-notification");
  if (cartNotification) {
    cartNotification.renderContents(event.detail);
  }
});

// Shopify's default add-to-cart AJAX function should trigger this event
// Only handle forms that don't already have ProductForm handling
document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
    // Skip forms that already have product-form element handling
    if (form.closest('product-form') || form.querySelector('product-form')) {
      return;
    }
    
    form.addEventListener("submit", (event) => {
      event.preventDefault();

      fetch(event.target.action, {
        method: "POST",
        body: new FormData(event.target),
        headers: {
          Accept: "application/json",
        },
      })
        .then((res) => res.json())
        .then((data) => {
          console.log("Product added to cart:", data);

          // Manually update the cart count
          updateCartCount();

          // Trigger cart notification
          document.dispatchEvent(
            new CustomEvent("cart:updated", { detail: data })
          );
        })
        .catch((err) => console.error("Error adding to cart:", err));
    });
  });

  // Function to update cart count dynamically
  function updateCartCount() {
    fetch("/cart.js")
      .then((res) => res.json())
      .then((cart) => {
        console.log("Updated cart data:", cart);
        const cartCountBubble = document.querySelector(".cart-count-bubble");

        if (cart.item_count > 0) {
          if (!cartCountBubble) {
            // If bubble does not exist, create it
            const newBubble = document.createElement("div");
            newBubble.className = "cart-count-bubble";
            newBubble.innerHTML = `
              <span aria-hidden="true">${cart.item_count}</span>
              <span class="visually-hidden">${cart.item_count} items in cart</span>
            `;
            document.querySelector(".cart").appendChild(newBubble);
          } else {
            // Update existing cart count
            cartCountBubble.querySelector(
              "span[aria-hidden='true']"
            ).textContent = cart.item_count;
          }
        } else {
          if (cartCountBubble) cartCountBubble.remove();
        }
      })
      .catch((err) => console.error("Error updating cart count:", err));
  }
});
